import type { PerformanceSnapshot } from "../types/performance";
import { Button } from "./ui/button";

function formatMetric(value: number) {
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(2)}M`;
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1)}k`;
  }
  return Math.round(value).toString();
}

function MetricRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-white/55">{label}</span>
      <span className="font-mono text-white/90">{value}</span>
    </div>
  );
}

function getFpsTone(fps: number) {
  if (fps >= 55) return "text-emerald-300 border-emerald-400/30 bg-emerald-400/10";
  if (fps >= 35) return "text-amber-200 border-amber-400/30 bg-amber-400/10";
  return "text-rose-200 border-rose-400/30 bg-rose-400/10";
}

function getDrawCallTone(drawCalls: number) {
  if (drawCalls <= 12) return "text-emerald-300 border-emerald-400/30 bg-emerald-400/10";
  if (drawCalls <= 24) return "text-amber-200 border-amber-400/30 bg-amber-400/10";
  return "text-rose-200 border-rose-400/30 bg-rose-400/10";
}

export function PerformanceStatsOverlay({
  stats,
  visible,
  onCopySnapshot,
  copied,
}: {
  stats: PerformanceSnapshot | null;
  visible: boolean;
  onCopySnapshot?: () => void;
  copied?: boolean;
}) {
  if (!visible) {
    return null;
  }

  return (
    <div className="fixed top-14 right-2 z-30 w-[240px] rounded-xl border border-white/12 bg-white/[0.08] p-3 backdrop-blur-xl shadow-2xl">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-white">Performance</div>
          <div className="text-[11px] text-white/50">Renderer debug HUD</div>
        </div>
        <div className="flex items-center gap-2">
          {onCopySnapshot ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 border-white/15 bg-white/5 px-2 text-[10px] text-white/80 hover:bg-white/10"
              onClick={onCopySnapshot}
            >
              {copied ? "Copied" : "Copy"}
            </Button>
          ) : null}
          <div className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] text-white/55">
            Live
          </div>
        </div>
      </div>

      {stats ? (
        <div className="flex flex-col gap-1.5">
          <div className="grid grid-cols-2 gap-2 pb-1">
            <div className={`rounded-lg border px-2 py-1 ${getFpsTone(stats.fps)}`}>
              <div className="text-[10px] uppercase tracking-[0.12em] opacity-75">FPS</div>
              <div className="font-mono text-sm font-semibold">{stats.fps.toFixed(0)}</div>
            </div>
            <div className={`rounded-lg border px-2 py-1 ${getDrawCallTone(stats.drawCalls)}`}>
              <div className="text-[10px] uppercase tracking-[0.12em] opacity-75">Draws</div>
              <div className="font-mono text-sm font-semibold">{formatMetric(stats.drawCalls)}</div>
            </div>
          </div>
          <MetricRow label="Avg FPS (10s)" value={stats.avgFps.toFixed(0)} />
          <MetricRow label="Min FPS (10s)" value={stats.minFps.toFixed(0)} />
          <MetricRow label="Frame" value={`${stats.frameTime.toFixed(1)} ms`} />
          <MetricRow label="DPR" value={stats.dpr.toFixed(2)} />
          <MetricRow label="Window" value={`${stats.sampleWindowSeconds.toFixed(0)} s`} />
          <MetricRow label="Triangles" value={formatMetric(stats.triangles)} />
          <MetricRow label="Points" value={formatMetric(stats.points)} />
          <MetricRow label="Particles" value={formatMetric(stats.particleCount)} />
          <MetricRow label="Geometries" value={formatMetric(stats.geometries)} />
          <MetricRow label="Textures" value={formatMetric(stats.textures)} />
          <MetricRow label="Programs" value={formatMetric(stats.programs)} />
          {typeof stats.jsHeapMb === "number" ? (
            <MetricRow label="JS Heap" value={`${stats.jsHeapMb.toFixed(1)} MB`} />
          ) : null}
        </div>
      ) : (
        <div className="text-xs text-white/60">Collecting frame data...</div>
      )}
    </div>
  );
}